// View helpers for the teams (joukkuevaraukset) screens. See kumpula/TEAMS.md.
//
// Pure functions of plain data, so they can be tested without a renderer. The domain logic -
// quota, the offer set, the tally, the spare clock - lives in utils/teamformats.js and
// utils/teamties.js and is SHARED WITH THE BACKEND; nothing here may re-derive any of it.
// What is here is only presentation: turning the availability grid into the shape the ordinary
// SimpleView slot picker already renders, and formatting.
//
// TIME FRAME, the thing to get right: every start/end in this feature is Helsinki wall clock
// LABELLED as UTC ('2026-10-14T18:00:00Z' means 18:00 in Helsinki). Parse with moment.utc and
// format without converting, exactly as components/ReservationUtil.js:6-14 describes for
// ordinary reservations. Converting shifts every time by the offset, silently.
import moment from 'moment'
// Registering the locales the app offers. moment.locale() is global and several screens set it
// on mount, so whatever it happens to be when a teams screen renders depends on where the user
// has been - which is how a Finnish user ended up reading Swedish weekday names. Everything here
// therefore formats with an EXPLICIT locale instead of trusting the global one.
import 'moment/locale/fi'
import 'moment/locale/sv'
import 'moment/locale/et'
import 'moment/locale/en-gb'
import { flattenRubbers } from './teamformats'

const WALLCLOCK_OUT = 'YYYY-MM-DDTHH:mm:ss'

const asWallclock = (m) => m.format(WALLCLOCK_OUT) + 'Z'

/**
 * i18next's language code to the moment locale that is actually loaded above.
 *
 * They are not the same vocabulary: the app calls Swedish 'se' (moment's 'se' is Northern Sami)
 * and Estonian 'ee' (moment's is 'et'), so passing the raw code through would silently fall back
 * to English. Unknown languages get Finnish, matching i18n's own fallbackLng.
 */
export const momentLocale = (lang) => {
  const code = String(lang || '').toLowerCase().split('-')[0]
  if (code === 'sv' || code === 'se') return 'sv'
  if (code === 'et' || code === 'ee') return 'et'
  if (code === 'en' || code === 'gb') return 'en-gb'
  return 'fi'
}

/**
 * appconfig.resources is an object keyed by resource id (see HorizontalView.js:236-238), which
 * is the only place a court's name can be found: teams-availability returns resourceid and
 * nothing else. A court that is not in the config still renders, labelled by its id, rather than
 * vanishing from the grid.
 */
export const courtsById = (appconfig, storeResources) => {
  const out = {}
  // The store copy first, then the appconfig copy on top: both are keyed by resource id and
  // carry displayName, but either can be missing or thin. appconfig.resources is either embedded
  // in the init document (a backend trigger does that for `ramu-` customers, leaving an EMPTY
  // OBJECT behind for a deleted resource) or filled in from the getResources call; the redux
  // slice is that same call's payload. Merging means a blank entry in one cannot erase a good
  // name in the other.
  const sources = [storeResources, appconfig && appconfig.resources]
  sources.forEach((source) => {
    if (!source || typeof source !== 'object') return
    Object.keys(source).forEach((id) => {
      const value = source[id]
      if (!value || typeof value !== 'object') return
      out[id] = { ...(out[id] || {}), ...value, id }
    })
  })
  return out
}

export const courtName = (courts, resourceid) => {
  const court = courts && courts[resourceid]
  return (court && (court.displayName || court.name)) || resourceid || ''
}

/**
 * teams-managerteams returns ties matched on teamid ONLY, so the array carries cancelled ties
 * and previous seasons as well (falcon/functions/teams.js:79-80 - the quota math is
 * season-filtered but the array is not). Every screen wants this filter first.
 */
export const openSeasonTies = (ties, team) =>
  (Array.isArray(ties) ? ties : []).filter((tie) => {
    if (!tie || tie.state !== 'open') return false
    if (!team) return true
    return tie.teamid === team.id && tie.seasonid === team.seasonid
  })

/**
 * One team's slice of the teams-managerteams payload: the team, its season, its format, its own
 * open ties for this season, and its quota block. `team` is null when the id is not one this
 * manager holds, which is what a screen renders "not found" from.
 *
 * Quota is PER TEAM and never pooled, even when one person manages several teams for the same
 * club - so this returns one team's block and nothing is ever summed across teams.
 */
export const teamContext = (data, teamid) => {
  const payload = data || {}
  const teams = Array.isArray(payload.teams) ? payload.teams : []
  const team = teams.find((t) => t && t.id === teamid) || null
  if (!team) return { team: null, season: null, format: null, ties: [], quota: null }
  const seasons = Array.isArray(payload.seasons) ? payload.seasons : []
  const formats = Array.isArray(payload.formats) ? payload.formats : []
  return {
    team,
    season: seasons.find((s) => s && s.id === team.seasonid) || null,
    format: formats.find((f) => f && f.id === team.formatid) || null,
    ties: openSeasonTies(payload.ties, team),
    quota: (payload.quota && payload.quota[team.id]) || null,
  }
}

/**
 * What to tell the manager about the two-choice rule right now — and, crucially, whether it is
 * a TO-DO or a WARNING.
 *
 * The rule: every opponent must be offered at least two dates, which is why the quota is
 * `homematches + 1`. `choiceStatus` (shared with the backend) says whether enough unclaimed
 * dates remain; it has no idea whether a shortfall means "hasn't started yet" or "lost dates
 * after the links went out", and those need opposite tones. A brand-new team is short by
 * definition - it has no ties at all - so treating the shortfall as a problem would greet every
 * manager with a warning on day one.
 *
 * The discriminator is whether anything has been claimed: no confirmations means no links have
 * been answered yet and this is still setup, so it is a to-do that states the target and the
 * reason. Once an opponent has confirmed, a shortfall means some later opponent is about to be
 * handed a single date, which is the case worth warning about. Running out of allowance is its
 * own case, because the manager cannot fix it alone.
 *
 * Returns null when there is nothing to say, including when `homematches` is unset - unfinished
 * config is not a bookable allowance and not the manager's problem to solve.
 */
export const choiceGuidance = (team, quota) => {
  const choices = quota && quota.choices
  if (!choices) return null
  const shortfall = Math.max(0, choices.needed - choices.unclaimed)
  if (!shortfall) return null

  const left =
    quota && typeof quota.quota === 'number' && typeof quota.used === 'number'
      ? quota.quota - quota.used
      : null
  const base = {
    needed: choices.needed,
    shortfall,
    unclaimed: choices.unclaimed,
    remaining: choices.remaining,
    confirmed: choices.confirmed,
    homematches: (team && team.homematches) || 0,
  }

  if (left !== null && left < shortfall) return { ...base, tone: 'warning', kind: 'noquota' }
  if (choices.confirmed > 0) return { ...base, tone: 'warning', kind: 'short' }
  if (choices.unclaimed === 0) return { ...base, tone: 'todo', kind: 'start' }
  return { ...base, tone: 'todo', kind: 'more' }
}

/**
 * A react-datepicker `filterDate` that allows only the weekdays the league actually plays on,
 * or `null` when that cannot be known.
 *
 * teams-managerteams deliberately withholds the season's `slots` (teams.js:135-136), and the
 * weekdays live on `slots[].days`, so today this returns null and the picker is bounded by the
 * season's date range alone. It reads an optional `season.weekdays` so that the moment the
 * backend supplies it - see kumpula/TEAMS_BACKEND_CHANGES.md - the filter starts working with no
 * further client change.
 */
export const seasonDayFilter = (season) => {
  const raw = season && Array.isArray(season.weekdays) ? season.weekdays : []
  const days = raw.map(Number).filter((d) => d >= 1 && d <= 7)
  if (!days.length) return null
  return (date) => days.indexOf(moment(date).isoWeekday()) > -1
}

/**
 * A stored 'YYYY-MM-DD' as '12.12.2026'. Falls back to the raw value rather than an empty string:
 * on the opponent's page a date is the thing they are being asked to confirm, so showing it
 * oddly formatted beats showing nothing.
 */
export const formatDate = (iso) => {
  const day = moment.utc(iso, 'YYYY-MM-DD', true)
  return day.isValid() ? day.format('D.M.YYYY') : iso || ''
}

/**
 * 'su 13.12.2026' - the weekday matters when an opponent is choosing between dates, and it is
 * the one localised part, so the language is explicit rather than left to the global locale.
 */
export const formatDateLong = (iso, lang) => {
  const day = moment.utc(iso, 'YYYY-MM-DD', true)
  return day.isValid() ? day.locale(momentLocale(lang)).format('dd D.M.YYYY') : iso || ''
}

/**
 * 'perjantai 30.10.2026' - the full weekday, for a tie's own heading.
 *
 * Longer than formatDateLong on purpose: a heading is read once and the weekday is what a
 * manager actually schedules around, while the two-letter form is for lists where the same
 * shape repeats down the page.
 */
export const formatDateWeekday = (iso, lang) => {
  const day = moment.utc(iso, 'YYYY-MM-DD', true)
  return day.isValid() ? day.locale(momentLocale(lang)).format('dddd D.M.YYYY') : iso || ''
}

/** '1.9.2026 - 30.4.2027', or '' when either end is missing or unparseable. */
export const formatDateRange = (from, to) => {
  const start = moment.utc(from, 'YYYY-MM-DD', true)
  const end = moment.utc(to, 'YYYY-MM-DD', true)
  if (!start.isValid() || !end.isValid()) return ''
  return start.format('D.M.YYYY') + ' - ' + end.format('D.M.YYYY')
}

/**
 * What the season's window allows right now, from bookingopens / bookingcloses. The backend is
 * the authority (it answers `season-closed` with one of three messages), but a screen that only
 * found out from a rejected call would be offering buttons that cannot work.
 *
 * Cancelling keeps working after the window shuts - teamContext on the backend deliberately
 * skips the window check for the cancel paths.
 */
export const seasonWindow = (season, todayiso) => {
  const opens = season && season.bookingopens
  const closes = season && season.bookingcloses
  if (!todayiso) return { canBook: false, state: 'unknown', opens, closes }
  if (opens && todayiso < opens) return { canBook: false, state: 'not-open', opens, closes }
  if (closes && todayiso > closes) return { canBook: false, state: 'closed', opens, closes }
  return { canBook: true, state: 'open', opens, closes }
}

/**
 * The start times at which a rubber of `duration` minutes fits inside ONE free range.
 *
 * The server does not snap free ranges to the clock and does not filter short holes - a
 * 40-minute gap is honest information (teams-utils.js:225-242). So the exact range start is
 * always offered (it is the only start that uses a hole completely), and after that the
 * step-aligned times, which is what anyone reading a booking grid expects to see.
 *
 * A rubber must fit ENTIRELY INSIDE ONE range: adjacent free ranges do not compose, and neither
 * do two adjacent season slots (teams-utils.js:187-198). Mirroring that here is what stops the
 * manager being offered a time the server then refuses with `outside-slot`.
 */
export const candidateStarts = (range, duration, step) => {
  if (!range || !(duration > 0)) return []
  const stepMinutes = step > 0 ? step : 30
  const from = moment.utc(range.start)
  const until = moment.utc(range.end)
  if (!from.isValid() || !until.isValid()) return []

  const latest = until.valueOf() - duration * 60000
  if (latest < from.valueOf()) return []

  const out = []
  const push = (m) => {
    const iso = asWallclock(m)
    if (out.indexOf(iso) < 0) out.push(iso)
  }

  push(from)

  const offset = (from.hours() * 60 + from.minutes()) % stepMinutes
  const cursor = from
    .clone()
    .add(offset ? stepMinutes - offset : stepMinutes, 'minutes')
    .seconds(0)
    .milliseconds(0)
  while (cursor.valueOf() <= latest) {
    push(cursor)
    cursor.add(stepMinutes, 'minutes')
  }
  return out
}

/**
 * Ties this team already holds on a given day.
 *
 * Two ties on one date is legal and occasionally deliberate - a club can host two encounters in
 * an evening - so this only warns; nothing blocks it. It is much more often a manager creating
 * the same date twice, which costs a unit of quota and cannot be edited away, only cancelled.
 */
export const tiesOnDate = (ties, date) =>
  !date ? [] : (Array.isArray(ties) ? ties : []).filter((tie) => tie && tie.date === date)

/**
 * Every date in the season the league actually plays, today forward.
 *
 * `weekdays` is the season's ISO weekdays from the backend. An EMPTY list means "unknown", not
 * "no days" - the same convention seasonDayFilter uses - so it yields every date in the window
 * rather than none. Capped so a malformed season cannot spin.
 */
export const seasonDates = (season, todayiso) => {
  const from = moment.utc((season && season.validstart) || '', 'YYYY-MM-DD', true)
  const until = moment.utc((season && season.validend) || '', 'YYYY-MM-DD', true)
  if (!from.isValid() || !until.isValid()) return []

  const days =
    season && Array.isArray(season.weekdays)
      ? season.weekdays.map(Number).filter((d) => d >= 1 && d <= 7)
      : []
  const today = moment.utc(todayiso, 'YYYY-MM-DD', true)
  const cursor = today.isValid() && today.isAfter(from) ? today.clone() : from.clone()

  const out = []
  while (cursor.isSameOrBefore(until, 'day') && out.length < 400) {
    if (!days.length || days.indexOf(cursor.isoWeekday()) > -1) out.push(cursor.format('YYYY-MM-DD'))
    cursor.add(1, 'day')
  }
  return out
}

/**
 * The season-availability response as render-ready rows: per date, one row per court with its
 * free ranges, plus the same fit verdict the tie builder shows for that day.
 *
 * The verdict comes from planDay, not from a second rule, so the overview and the date itself
 * cannot disagree - including the singles-before-doubles ordering.
 *
 * A court with a slot but nothing free keeps its row with no ranges: the Excel this replaces
 * lists the court either way, and "that court is full" is information.
 */
export const availabilityTable = (dates, options) => {
  const opts = options || {}
  const courts = opts.courts || {}
  return (Array.isArray(dates) ? dates : []).map((entry) => {
    const slots = entry && Array.isArray(entry.slots) ? entry.slots : []

    const byCourt = {}
    const windows = {}
    slots.forEach((slot) => {
      if (!slot || !slot.resourceid) return
      if (!byCourt[slot.resourceid]) byCourt[slot.resourceid] = []
      ;(Array.isArray(slot.free) ? slot.free : []).forEach((range) => {
        if (range && range.start && range.end) byCourt[slot.resourceid].push(range)
      })
      // Two slots can cover one court on one date (17-19 and 20-22 as separate rows), so the
      // court's window is their span. The gap between them is not free and is not offered, so
      // it draws as busy either way.
      if (slot.start && slot.end) {
        const had = windows[slot.resourceid]
        windows[slot.resourceid] = {
          start: had && had.start < slot.start ? had.start : slot.start,
          end: had && had.end > slot.end ? had.end : slot.end,
        }
      }
    })

    const rows = Object.keys(byCourt)
      .map((id) => {
        const court = courts[id] || {}
        return {
          resourceid: id,
          name: courtName(courts, id),
          order: typeof court.order === 'number' ? court.order : 0,
          // The season slot's own window, when the backend sends it (TEAMS_BACKEND_CHANGES.md
          // 4). Until it does this is null and the grid falls back to the free ranges, which
          // means time outside the league's hours is drawn as busy rather than as nothing.
          window: windows[id] || null,
          ranges: byCourt[id]
            .slice()
            .sort((a, b) => String(a.start).localeCompare(String(b.start))),
        }
      })
      .sort((a, b) => a.order - b.order || String(a.name).localeCompare(String(b.name)))

    const plan = planDay({ slots }, opts.format, { courts, step: opts.step })
    return {
      date: (entry && entry.date) || '',
      courts: rows,
      anyFree: rows.some((row) => row.ranges.length > 0),
      fits: plan.fits,
      expected: plan.expected,
    }
  })
}

// ---- the half-hour grid ------------------------------------------------------------------
// The overview's strips are a TIME-OF-DAY grid, so everything below counts minutes from midnight
// rather than comparing instants: that is what lets one set of columns serve every date in the
// season, which is the whole point - a column can be scanned down the page.

const minutesOfDay = (m) => m.hours() * 60 + m.minutes()

/** A {start,end} pair as minutes from midnight, or null if it is not a usable range. */
const rangeMinutes = (range) => {
  const from = moment.utc(range && range.start)
  const until = moment.utc(range && range.end)
  if (!from.isValid() || !until.isValid()) return null
  const start = minutesOfDay(from)
  // A slot configured to end '23:59' arrives here as 23:59:59.999 (teams-utils slotsForDate), so
  // round any part-minute up. Nothing in this feature books across midnight, so an end at or
  // before the start can only mean end-of-day.
  let end = minutesOfDay(until) + (until.seconds() || until.milliseconds() ? 1 : 0)
  if (end <= start) end = 24 * 60
  return { from: start, to: end }
}

// 64 cells is ~16 hours at the usual 30-minute step. Beyond that the strip is unreadable at any
// width, and it means the season is mis-configured rather than that we should try to draw it.
const MAX_GRID_CELLS = 64

/**
 * The columns every date's strip shares: the widest span any court is open anywhere in the
 * season, snapped outward to the step.
 *
 * ONE window for the whole season, not one per date, so 20:00 sits in the same column on every
 * row and the eye can run down it. The cost is that a date whose slot is shorter shows grey at
 * the edges - which is true, in the sense that matters: nothing can be booked there.
 *
 * Falls back to the free ranges when the backend does not send slot windows, so it works with
 * what is deployed today. Null when there is nothing to draw.
 */
export const seasonGridWindow = (table, step) => {
  const stepMinutes = step > 0 ? step : 30
  let earliest = null
  let latest = null

  ;(Array.isArray(table) ? table : []).forEach((entry) => {
    ;(entry && Array.isArray(entry.courts) ? entry.courts : []).forEach((court) => {
      const bounds =
        court.window && court.window.start && court.window.end
          ? [court.window]
          : Array.isArray(court.ranges)
          ? court.ranges
          : []
      bounds.forEach((bound) => {
        const span = rangeMinutes(bound)
        if (!span) return
        if (earliest === null || span.from < earliest) earliest = span.from
        if (latest === null || span.to > latest) latest = span.to
      })
    })
  })

  if (earliest === null || latest === null || latest <= earliest) return null

  const from = Math.floor(earliest / stepMinutes) * stepMinutes
  const to = Math.ceil(latest / stepMinutes) * stepMinutes
  const count = (to - from) / stepMinutes
  if (count < 1 || count > MAX_GRID_CELLS) return null

  const columns = []
  for (let at = from; at < to; at += stepMinutes) {
    columns.push({
      from: at,
      to: at + stepMinutes,
      hourStart: at % 60 === 0,
      // Whole hours only: a number under a 12px cell every half hour is noise, not an axis.
      label: at % 60 === 0 ? String(Math.floor(at / 60) % 24) : '',
    })
  }
  return { from, to, step: stepMinutes, columns }
}

/**
 * One court's row of cells against those columns: 'free', 'busy' or 'outside'.
 *
 * A cell is free only when it lies ENTIRELY inside one free range. Half a free cell cannot take
 * a rubber - and adjacent free ranges never compose, which is why this is `some` over the ranges
 * and not a merged coverage test - so painting it white would promise a time the server refuses.
 *
 * 'outside' needs the slot window, which nothing sends yet; until then time outside the league's
 * hours is indistinguishable from a booking and draws as busy. Both mean "you cannot have this".
 */
export const gridCells = (court, columns) => {
  const free = (court && Array.isArray(court.ranges) ? court.ranges : [])
    .map(rangeMinutes)
    .filter((span) => span)
  const window = court && court.window ? rangeMinutes(court.window) : null

  return (Array.isArray(columns) ? columns : []).map((col) => {
    if (window && (col.from < window.from || col.to > window.to)) return 'outside'
    return free.some((span) => col.from >= span.from && col.to <= span.to) ? 'free' : 'busy'
  })
}

/** When a rubber starting at `start` would end, in the same wall-clock-as-UTC frame. */
export const rubberEnd = (start, duration) => {
  const from = moment.utc(start)
  if (!from.isValid() || !(duration > 0)) return ''
  return asWallclock(from.clone().add(duration, 'minutes'))
}

const overlaps = (startA, durationA, startB, durationB) => {
  const a = moment.utc(startA)
  const b = moment.utc(startB)
  if (!a.isValid() || !b.isValid()) return false
  const aEnd = a.valueOf() + durationA * 60000
  const bEnd = b.valueOf() + durationB * 60000
  return a.valueOf() < bEnd && b.valueOf() < aEnd
}

/**
 * The rubbers the format still calls for, given the placements already chosen — one entry each,
 * longest first. This is the checklist the manager works down while laying out a tie.
 *
 * Counts against `flattenRubbers`, the shared definition, so two rows of the same type with
 * different lengths stay distinct and an exhausted row simply stops appearing.
 */
export const remainingRubbers = (format, picks) => {
  const counts = {}
  ;(Array.isArray(picks) ? picks : []).forEach((pick) => {
    if (pick && pick.rubberid) counts[pick.rubberid] = (counts[pick.rubberid] || 0) + 1
  })
  const out = []
  flattenRubbers(format).forEach((rubber) => {
    if (counts[rubber.rubberid] > 0) {
      counts[rubber.rubberid] -= 1
      return
    }
    out.push(rubber)
  })
  return out
}

/**
 * Greedily place every rubber the format still needs into this day's availability — earliest
 * fit, longest rubber first, respecting placements already chosen.
 *
 * Two jobs, deliberately the same function so they can never disagree:
 *  - the "does this date have room?" readout the manager scans dates with;
 *  - the proposal auto-arrange offers, which the manager then adjusts.
 *
 * Nothing here is booked: this runs entirely before the save, so a proposal costs nothing to
 * change. It is a heuristic, not an optimum - a date it says does not fit may still fit in some
 * arrangement it did not try, which is why the manual grid always stays available.
 */
export const planDay = (availability, format, options) => {
  const opts = options || {}
  const picks = Array.isArray(opts.picks) ? opts.picks : []
  const pending = picks.slice()
  const placed = []

  const place = (rubber, notBefore) => {
    const slots = availabilitySlots(availability, rubber, {
      courts: opts.courts,
      step: opts.step,
      picks: pending,
      notBefore,
    })
    if (!slots.length || !slots[0].courts.length) return false
    const slot = slots[0]
    const court = slot.courts[0]
    const pick = {
      rubberid: rubber.rubberid,
      type: rubber.type,
      duration: rubber.duration,
      resourceid: court.id,
      courtname: court.displayName,
      start: slot.start,
      end: rubberEnd(slot.start, rubber.duration),
    }
    placed.push(pick)
    pending.push(pick)
    return true
  }

  const remaining = remainingRubbers(format, picks)
  const singles = remaining.filter((r) => r.type !== 'double')
  const doubles = remaining.filter((r) => r.type === 'double')

  // Singles first, earliest fit, in parallel wherever courts allow.
  singles.forEach((rubber) => place(rubber, null))

  // Wall-clock strings in one format compare lexically, so no parsing is needed here.
  const singlePicks = pending.filter((p) => p && p.type !== 'double')
  const endOfSingles = singlePicks.reduce((max, p) => (p.end > max ? p.end : max), '')
  const lastSingleStart = singlePicks.reduce((max, p) => (p.start > max ? p.start : max), '')
  // Two singles means a small squad: the same people play the doubles, so it cannot overlap at
  // all. With three or more there is usually a spare pair, so sharing the last slot is possible
  // when courts are scarce.
  const smallSquad = singlePicks.length <= 2

  let rule = doubles.length ? (endOfSingles ? 'after-singles' : 'unordered') : 'singles-only'

  doubles.forEach((rubber) => {
    if (!endOfSingles) {
      place(rubber, null)
      return
    }
    if (place(rubber, endOfSingles)) return
    // Not enough evening left to run the doubles after the singles. On three or more singles the
    // venue's real fallback is "2 singles, then 1 single + the double", so allow the double to
    // start alongside the last single - but never for a small squad, where it would mean the
    // same players on two courts at once.
    if (!smallSquad && lastSingleStart && place(rubber, lastSingleStart)) {
      rule = 'with-last-single'
      return
    }
    // Leaving it unplaced is the honest answer: the day does not work for this format, and the
    // "fits" readout says so rather than proposing something unplayable.
    rule = 'doubles-do-not-fit'
  })

  const expected = flattenRubbers(format).length
  return { placed, fits: picks.length + placed.length, expected, rule }
}

/**
 * The availability response as `dailyFreeTimes`, the shape SimpleView already renders:
 * one entry per start time, each carrying the courts free at that time, each court carrying the
 * lengths on offer (SimpleView.js:101-130).
 *
 * There is exactly one length - the format row's - so the picker cannot express any other. That
 * is spec invariant 1 ("no duration ever crosses the wire") made true by construction in the UI
 * as well as in the request.
 *
 * No price: teams-availability drops the season slot's price (teams.js:312-320), so the cost is
 * only knowable after booking. Render the grid with `recurrentsales` set, which is what
 * suppresses SimpleView's price badge (SimpleView.js:208) - otherwise it prints "null €".
 */
export const availabilitySlots = (availability, row, options) => {
  if (!availability || !row || !(row.duration > 0)) return []
  const opts = options || {}
  const courts = opts.courts || {}
  // Placements chosen but not yet saved hold no reservation, so the server still reports their
  // courts as free. Excluding them here is what stops the manager placing two rubbers of the
  // same tie on one court at one time.
  const picks = Array.isArray(opts.picks) ? opts.picks : []
  // Used by the planner to keep the doubles after the singles; wall-clock strings in one format
  // compare lexically.
  const notBefore = opts.notBefore || ''
  const byStart = {}

  ;(Array.isArray(availability.slots) ? availability.slots : []).forEach((entry) => {
    if (!entry || !entry.resourceid) return
    ;(Array.isArray(entry.free) ? entry.free : []).forEach((range) => {
      candidateStarts(range, row.duration, opts.step).forEach((start) => {
        if (notBefore && start < notBefore) return
        const clash = picks.some(
          (pick) =>
            pick &&
            pick.resourceid === entry.resourceid &&
            overlaps(start, row.duration, pick.start, pick.duration)
        )
        if (clash) return
        if (!byStart[start]) byStart[start] = {}
        // A court can appear under two overlapping season slots; the first wins, so the grid
        // shows it once.
        if (byStart[start][entry.resourceid]) return
        const court = courts[entry.resourceid] || {}
        byStart[start][entry.resourceid] = {
          id: entry.resourceid,
          displayName: courtName(courts, entry.resourceid),
          category: court.category || '',
          venue: court.venue || '',
          order: typeof court.order === 'number' ? court.order : 0,
          slotid: entry.slotid,
          times: [{ duration: row.duration, rubberid: row.rubberid }],
        }
      })
    })
  })

  return Object.keys(byStart)
    .sort()
    .map((start) => ({
      start,
      // SimpleView keys its rows off `slot.key` (SimpleView.js:268), so every entry needs a
      // stable unique one or React collapses the rows into each other. The start time is unique
      // per entry by construction.
      key: start,
      courts: Object.keys(byStart[start])
        .map((id) => byStart[start][id])
        .sort((a, b) => a.order - b.order || String(a.displayName).localeCompare(String(b.displayName))),
    }))
}

/**
 * The tally for a tie whose rubbers the BACKEND already joined.
 *
 * teams-managerteams delivers ties[].rubbers[] with a `status` of 'booked' | 'cancelled' |
 * 'missing', derived server-side by the same shared module the venue screen uses. So this reads
 * that status rather than re-deriving it, and takes `expected` from the format via
 * flattenRubbers - never from rubbers.length, which counts intentions rather than bookings.
 *
 * Use teamties.rubberTally instead wherever the raw reservations are in hand.
 */
export const tallyFromRows = (rubbers, format) => {
  const rows = Array.isArray(rubbers) ? rubbers : []
  let booked = 0
  let gone = 0
  rows.forEach((r) => {
    if (r && r.status === 'booked') booked += 1
    else gone += 1
  })
  return { booked, gone, expected: flattenRubbers(format).length }
}

/**
 * Ties that do not hold every rubber their format calls for.
 *
 * What this is for: the opponent's page renders the rubbers as they stand when the link is
 * opened, so a link sent before the courts are booked shows an empty page. Warning about that
 * unconditionally trains the manager to ignore it, so it is worth saying only when one of the
 * ties actually being offered is short.
 *
 * Counts booked rubbers, never rubbers.length - a reservation an admin cancelled leaves its
 * entry behind and must not read as held.
 */
export const incompleteTies = (ties, format) =>
  (Array.isArray(ties) ? ties : []).filter((tie) => {
    const tally = tallyFromRows(tie && tie.rubbers, format)
    return tally.booked < tally.expected
  })

/**
 * 'ma 14.10. 18:00 - 19:30', with '(+1)' when the rubber runs past midnight.
 *
 * The weekday is the only localised part, and it is explicit: without a language the global
 * moment locale decides, which is whatever the last screen the user visited happened to set.
 *
 * `withDate: false` gives '18:00 - 19:30' for a list that already sits under its own date - every
 * rubber of a tie is on that tie's date, so repeating it three times says nothing. The '(+1)'
 * stays either way; without the date it is the only thing that marks a rubber past midnight.
 */
export const formatRubberTime = (start, end, lang, options) => {
  const locale = momentLocale(lang)
  const withDate = !options || options.withDate !== false
  const from = moment.utc(start).locale(locale)
  if (!from.isValid()) return ''
  const base = (withDate ? from.format('dd D.M.') + ' ' : '') + from.format('H:mm')
  const until = moment.utc(end).locale(locale)
  if (!until.isValid()) return base
  return base + ' - ' + until.format('H:mm') + (until.isSame(from, 'day') ? '' : ' (+1)')
}

/**
 * The spare's 24-hour clock, from quota[teamid].spare.hoursleft.
 *
 * `null` when no clock is running. `overdue` means the window has passed and the backend has not
 * released it yet - shown, never clamped away, because it is the signal that the release job is
 * not running for this venue (it is opt-in per venue via TEAM_CUSTOMERS in teams-cron.js, and
 * the list is empty today). The job also runs only hourly at :07, so a live countdown can read
 * up to an hour optimistic.
 */
export const spareCountdown = (spare) => {
  const hours = spare && typeof spare.hoursleft === 'number' ? spare.hoursleft : null
  if (hours === null || isNaN(hours)) return null
  return { overdue: hours <= 0, hours: Math.max(0, Math.round(hours)) }
}

/**
 * The opponent's link. No callable returns it - the canonical shape lives in
 * teams-utils.js:315-320 as `https://<venue host>/teams/accept/<token>`, with the token as a
 * PATH segment so it never lands in a Referer header.
 *
 * `pathPrefix` is '' in production; on utest every tenant is served from one origin and the
 * tenant is the first path segment, so the link needs it to resolve.
 */
export const acceptUrl = (host, token, pathPrefix) => tokenUrl('accept', host, token, pathPrefix)

/**
 * The same token as a MENU of every candidate date: `/teams/offer/<token>`.
 *
 * One teams-accept read answers both pages - it returns the token's own tie AND `alternatives` -
 * so which one the opponent gets is decided by the path they were sent, not by the backend. That
 * is what keeps the two links meaningfully different: a per-date link confirms the date the
 * manager offered, and the menu link lets the opponent choose. Before this split both printed
 * the same URL under two headings promising different things.
 */
export const offerUrl = (host, token, pathPrefix) => tokenUrl('offer', host, token, pathPrefix)

const tokenUrl = (kind, host, token, pathPrefix) => {
  if (!host || !token) return ''
  const trimmed = String(host).replace(/\/+$/, '')
  const base = /^https?:\/\//i.test(trimmed) ? trimmed : 'https://' + trimmed
  return base + (pathPrefix || '') + '/teams/' + kind + '/' + token
}

/**
 * Which origin an acceptance link should point at.
 *
 * In production a tenant has its own domain and no path prefix, so the link is
 * `https://<hosts[0]>/teams/accept/<token>` - the same shape the backend mints in
 * teamAcceptUrl. On utest and localhost one origin serves every tenant and the tenant is the
 * FIRST PATH SEGMENT, so the link has to point at the origin the manager is actually on:
 * combining hosts[0] with the prefix would produce a path that exists on neither.
 */
export const acceptHost = (appconfig) => {
  const prefix = (appconfig && appconfig.pathPrefix) || ''
  if (prefix) {
    return typeof window !== 'undefined' && window.location ? window.location.origin : ''
  }
  return (appconfig && appconfig.hosts && appconfig.hosts[0]) || ''
}

/**
 * The candidate dates an opponent may choose from, out of a teams-accept read.
 *
 * Returns null when the backend has not sent `alternatives` at all, which is the signal to fall
 * back to the old one-tie-per-link page - so an empty array (a team with no open ties) and a
 * missing field must stay distinguishable. See kumpula/TEAMS_BACKEND_CHANGES.md §2.
 *
 * A claimed date stays in the list, marked, naming the team that took it: an opponent who was
 * offered that date should learn it is gone rather than watch it vanish between visits.
 */
export const acceptOptions = (view) => {
  const list = view && Array.isArray(view.alternatives) ? view.alternatives : null
  if (!list) return null
  return list
    .filter((option) => option && option.token)
    .map((option) => ({
      token: option.token,
      date: option.date || '',
      claimed: !!option.claimed,
      opponentteam: option.opponentteam || '',
      rubbers: Array.isArray(option.rubbers) ? option.rubbers : [],
    }))
    .sort((a, b) => String(a.date).localeCompare(String(b.date)))
}

/**
 * The single link that shows an opponent every candidate date: the `/teams/offer/` form.
 *
 * Takes the earliest UNCLAIMED tie's token. Any token lists the same alternatives, so for the
 * menu itself the choice does not matter - but the token is also what a chosen date is claimed
 * with, and a link built on a claimed tie is one keystroke away from a dead page if someone
 * opens it as `/teams/accept/`. Preferring an unclaimed tie costs nothing and is right either
 * way.
 *
 * Falls back to any open tie when every date is claimed, so the link still resolves to something
 * that explains itself rather than to nothing. Empty when there are no open ties or no host.
 */
export const offerLink = (ties, { host, pathPrefix }) => {
  const open = openSeasonTies(ties)
    .slice()
    .sort((a, b) => String(a.date).localeCompare(String(b.date)))
    .filter((tie) => tie.token)
  const unclaimed = open.find((tie) => (tie.confirmation && tie.confirmation.state) !== 'confirmed')
  const chosen = unclaimed || open[0]
  return chosen ? offerUrl(host, chosen.token, pathPrefix) : ''
}

/**
 * One copyable block per opponent: the candidate dates still unclaimed, each line naming its
 * date, so the manager can paste one opponent's choices into one mail rather than sending an
 * undifferentiated dump. Claimed dates are dropped - they are no longer on offer.
 */
export const linkLines = (ties, { host, pathPrefix, lang }) =>
  openSeasonTies(ties)
    .filter((tie) => (tie.confirmation && tie.confirmation.state) !== 'confirmed')
    .slice()
    .sort((a, b) => String(a.date).localeCompare(String(b.date)))
    .map((tie) => ({
      tieid: tie.id,
      date: tie.date,
      url: acceptUrl(host, tie.token, pathPrefix),
      text: moment.utc(tie.date, 'YYYY-MM-DD', true).isValid()
        ? formatDateLong(tie.date, lang) + ': ' + acceptUrl(host, tie.token, pathPrefix)
        : acceptUrl(host, tie.token, pathPrefix),
    }))
